import { action, computed } from 'mobx';
import { orderBy } from 'lodash';
import moment from 'moment';

import {
  AGENT_TYPES,
  AGENT_CAPABILITIES,
  AGENT_STATUSES,
  RECENTLY_ADDED_DAYS
} from '@kentik/ui-shared/synthetics/constants';

import Collection from 'core/model/Collection';
import AgentModel from './AgentModel';
import { getOptionsForAgents } from './utils';

class AgentCollection extends Collection {
  agentCacheDurationMs = 300000; // 5 min

  agentCachePromise;

  get model() {
    return AgentModel;
  }

  get url() {
    return '/api/ui/synthetics/agents/stats';
  }

  /**
   * Agents actually serving tests lead; everything else falls in behind them by name.
   * @returns {{field: string, direction: 'desc'}}
   */
  get defaultSortState() {
    return { field: 'hasTests', direction: 'desc' };
  }

  // Applied after whichever column is sorted, so ties never come back in arbitrary order.
  get secondarySort() {
    return { field: 'agent_alias', direction: 'asc' };
  }

  // Restrictive: without this, search matches every raw attribute including metadata blobs.
  // Names resolve as `model.get(name) || model[name]`, so computed getters work here.
  get filterFieldWhitelist() {
    return new Set(['agent_alias', 'label', 'ips', 'locationInfoValue', 'semVersion', 'id']);
  }

  // The stats endpoint returns global pending agents to everyone; only sudoers may see them.
  get defaultPresetFilter() {
    return {
      fn: (agent) =>
        agent.get('agent_status') !== AGENT_STATUSES.WAIT ||
        this.store.$auth.isSudoer ||
        agent.get('agent_type') === AGENT_TYPES.PRIVATE
    };
  }

  @computed
  get recentlyAddedAgents() {
    const currentMoment = moment();
    const models = this.privateAgents.filter(
      (model) => currentMoment.diff(moment(model.get('edate')), 'days') <= RECENTLY_ADDED_DAYS
    );

    return orderBy(models, ['edate'], ['desc']);
  }

  @action
  async syncAgentHealth() {
    const { $syn } = this.store;
    // just return promise if sync already in progress.
    if (this.agentHealthSyncPromise) {
      return this.agentHealthSyncPromise;
    }

    // TODO: deprecate browserAgents #7660
    const data = { browserIds: [], privateIds: [], globalIds: [] };
    const globalIds = new Set();

    this.privateAgents.forEach((agent) => data.privateIds.push(agent.id));
    this.globalAgents.forEach((agent) => globalIds.add(agent.id));

    // Put browser ids in with globals batch for now so they get processed on server, results not sorted by type.
    this.browserAgents.forEach((agent) => globalIds.add(agent.id));
    this.pendingAgents.forEach((agent) => {
      if (agent.get('agent_type') === AGENT_TYPES.PRIVATE) {
        data.privateIds.push(agent.id);
      } else {
        globalIds.add(agent.id);
      }
    });

    data.globalIds = Array.from(globalIds);

    this.agentHealthSyncPromise = $syn.requests
      .fetchAgentHealth(data)
      .then(
        action('updateAgentHealth', (agentHealthMap) => {
          [...data.privateIds, ...data.globalIds, ...data.browserIds].forEach((agentId) => {
            const agent = this.get(agentId);

            // a successful sync that omits an agent means it reported nothing, which reads as offline
            if (agent) {
              agent.set(agentHealthMap[agentId] || { last_seen: null, health: null });
            }
          });

          this.setLastUpdated();
        })
      )
      // cleared either way, or one rejection would make every later sync reuse the failure
      .finally(
        action(() => {
          this.agentHealthSyncPromise = null;
        })
      );

    return this.agentHealthSyncPromise;
  }

  @action
  async findAgentByChallenge(challenge, metadata) {
    const { $syn } = this.store;
    return $syn.requests.fetchAgentByChallenge({ challenge, metadata }).then((res) => this.add(res)[0]);
  }

  @computed
  get agentOptions() {
    return this.privateAgentOptions.concat(this.globalAgentOptions);
  }

  /*
  A mapping of functions used as callbacks to the filters that drive the agent type @computeds below
  For example:
    - this.privateAgents is the unfiltered list of agents of type private
    - this.privateAgentsFiltered is the filtered list of agents of type private
    - both of these @computeds would want to use the same filter callback below but a different getter off the collection
*/
  AGENT_FILTER_CALLBACKS = {
    private: (agent) => agent.get('agent_status') === 'OK' && agent.get('agent_type') === AGENT_TYPES.PRIVATE,
    global: (agent) => agent.get('agent_status') === 'OK' && agent.get('agent_type') === AGENT_TYPES.GLOBAL,
    browser: (agent) =>
      agent.get('agent_status') === 'OK' &&
      agent.get('agent_type') === AGENT_TYPES.GLOBAL &&
      agent.get('metadata')?.capabilities?.includes(AGENT_CAPABILITIES.WEB),
    pending: (agent) =>
      agent.get('agent_status') === 'WAIT' &&
      (this.store.$auth.isSudoer || agent.get('agent_type') === AGENT_TYPES.PRIVATE),
    staticIp: (agent) =>
      agent.get('agent_status') === 'OK' && agent.get('metadata')?.capabilities?.includes(AGENT_CAPABILITIES.STATIC_IP),
    cloud: (agent) => agent.get('agent_status') === 'OK' && !!agent.get('metadata')?.cloud_provider,
    all: (agent) => agent.get('agent_status') === 'OK'
  };

  @computed
  get privateAgents() {
    return this.get().filter(this.AGENT_FILTER_CALLBACKS.private);
  }

  @computed
  get privateAgentsFiltered() {
    return this.models.filter(this.AGENT_FILTER_CALLBACKS.private);
  }

  @computed
  get globalAgents() {
    return this.get().filter(this.AGENT_FILTER_CALLBACKS.global);
  }

  @computed
  get globalAgentsFiltered() {
    return this.models.filter(this.AGENT_FILTER_CALLBACKS.global);
  }

  @computed
  get browserAgents() {
    return this.get().filter(this.AGENT_FILTER_CALLBACKS.browser);
  }

  @computed
  get browserAgentsFiltered() {
    return this.models.filter(this.AGENT_FILTER_CALLBACKS.browser);
  }

  @computed
  get pendingAgents() {
    return this.get().filter(this.AGENT_FILTER_CALLBACKS.pending);
  }

  @computed
  get pendingAgentsFiltered() {
    return this.models.filter(this.AGENT_FILTER_CALLBACKS.pending);
  }

  @computed
  get hasStaticIpAgents() {
    return this.get().some(this.AGENT_FILTER_CALLBACKS.staticIp);
  }

  @computed
  get cloudAgents() {
    return this.get().filter(this.AGENT_FILTER_CALLBACKS.cloud);
  }

  @computed
  get cloudAgentsFiltered() {
    return this.models.filter(this.AGENT_FILTER_CALLBACKS.cloud);
  }

  @computed
  get pendingAgentOptions() {
    return this.getOptionsForAgents(this.pendingAgents);
  }

  @computed
  get privateAgentOptions() {
    return this.getOptionsForAgents(this.privateAgents);
  }

  @computed
  get globalAgentOptions() {
    return this.getOptionsForAgents(this.globalAgents, { showAsNameInLabel: true });
  }

  @computed
  get browserAgentOptions() {
    const privateBrowserAgents = this.privateAgentOptions.filter((a) =>
      a.capabilities?.includes(AGENT_CAPABILITIES.WEB)
    );
    return this.getOptionsForAgents(this.browserAgents).concat(privateBrowserAgents);
  }

  @computed
  get allAgents() {
    return this.get().filter(this.AGENT_FILTER_CALLBACKS.all);
  }

  @computed
  get allAgentsOptions() {
    return this.getOptionsForAgents(this.get());
  }

  getOptionsForAgents(agents, opts) {
    return getOptionsForAgents(agents, opts);
  }

  getOptionsForAgentsByIds(ids) {
    // get all the agents by id, filtering out agents that may no longer exist
    const agents = ids.map((id) => this.get(id)).filter((agent) => !!agent);
    return this.getOptionsForAgents(agents);
  }

  getAgentsBySiteId(siteId) {
    return this.privateAgents.filter((agent) => parseInt(agent.get('site_id')) === parseInt(siteId));
  }

  getAgentsByID(ids = []) {
    return ids.map((id) => this.get(id)).filter((agent) => !!agent);
  }

  getTargetAgentsByIps(ips = []) {
    if (!Array.isArray(ips)) {
      ips = [ips];
    }

    return this.get().filter((model) => model.ips.find((ip) => ips.includes(ip)));
  }

  isMtuSupported(agentIds = []) {
    if (agentIds.length > 0) {
      return agentIds.every((agentId) => this.get(agentId).isMtuSupported);
    }

    return false;
  }
}

export default AgentCollection;
